import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNewlyAddedProducts } from './api/useNewlyAddedProducts';
import { deserializeFilter } from '../../shared/utils/deserializeFilter';
import { useQueryParams } from '../../shared/hooks/useQueryParams';
import { ProductSorting } from '../../../models/Product';
import queryString from 'query-string';
import { useLocation, useParams } from 'react-router';
import { LibxChangeProductPageBuilder } from './shared/LibxChangeProuctPageBuilder';
import { DISPLAY_TYPE_MINIMAL } from '../../store/displayType/types';
import { FetchAggregations } from '../../api/listContent/fetchAggregations';
import { fetchNewlyAggregations } from './api/fetchNewlyAddedAggregations';
import { FetchAggregationsQuickSearch } from '../../api/listContent/fetchAggregationsQuickSearch';
import { fetchNewlyAddedAggregationsQuickSearch } from './api/fetchNewlyAddedAggregationsQuickSearch';
import { QueryKeyBuilder } from '../../shared/filter/useAggregationsQuery';
import { AppState } from '../../store/appState';
import { defaultVisibleFilterGroups } from '../../shared/filter/defaultVisibleFilterGroups';
import { useAgeCategorySearchParam } from '../../shared/utils/useAgeCategorySearchParam';

const LIBX_NEWLY_ADDED_DOCUMENT_TITLE = 'LIBXchange | Newly Added';

const SORT_BY_QUERY_PARAM = 'sortBy';
const PAGE_QUERY_PARAM = 'page';

export const DEFAULT_PAGE_SIZE = 55;

export const NewlyAddedPage = () => {
    const { search } = useLocation();

    const { shopLanguage } = useParams<{ shopLanguage?: string }>();

    const authentication = useSelector((state: AppState) => state.authentication);

    /**
     * The `displayFilter` query param is either there or not. There is no value attached to it.
     * Examples:
     *      with:       `/collection/by-genre/all?displayFilter
     *      without:    `/collection/by-genre/all
     *
     * Parsing this query param with the `query-string` library will result in either null value or undefined.
     * This is the way how we can identify if the filter should be open or not.
     * Unfortunately, we can not replicate this behaviour with the `useQueryParams` hook.
     *
     * Of course it would be nicer to have a boolean value.
     * But that is a breaking change in regard to the URL structure.
     */
    const { displayFilter: displayFilterQueryParam } = queryString.parse(search);
    const displayFilter = displayFilterQueryParam === null;

    const {
        page: { value: pageParam = 1 },
        pageSize: { value: pageSizeParam },
        sortBy: { value: sortByParam },
        filter: { value: filterQueryParam },
        display: { value: display }
    } = useQueryParams({
        [PAGE_QUERY_PARAM]: 'integer',
        [SORT_BY_QUERY_PARAM]: 'string',
        pageSize: 'integer',
        filter: 'string',
        display: 'string'
    });
    const page = pageParam - 1 ?? 0;
    const sortBy = sortByParam ?? ProductSorting.RELEASE_DATE_NEW_TO_OLD;
    const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
    const { value: ageCategory } = useAgeCategorySearchParam();
    const filter = deserializeFilter(filterQueryParam);

    const { products, isLoading: isProductsLoading } = useNewlyAddedProducts({
        pagination: {
            page,
            pageSize,
            sortBy
        },
        filterMap: filter,
        ageCategory,
        shopLanguage
    });

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(
        () => defaultVisibleFilterGroups(authentication, ageCategory),
        [ageCategory, authentication]
    );
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'newlyAddedPage',
            'aggregation',
            ageCategory,
            filters,
            expandedFilterGroups,
            shopLanguage
        ],
        [ageCategory, shopLanguage]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchNewlyAggregations({ ...options, ageCategory, shopLanguage }),
        [ageCategory, shopLanguage]
    );
    const fetchAggregationsQuickSearch = useCallback<FetchAggregationsQuickSearch>(
        options => fetchNewlyAddedAggregationsQuickSearch({ ...options, ageCategory, shopLanguage }),
        [ageCategory, shopLanguage]
    );
    //endregion

    return (
        <LibxChangeProductPageBuilder
            loading={isProductsLoading}
            products={products}
            displayFilter={displayFilter}
            documentTitle={LIBX_NEWLY_ADDED_DOCUMENT_TITLE}
            pageQueryParam={PAGE_QUERY_PARAM}
            sortByQueryParam={SORT_BY_QUERY_PARAM}
            displayType={display ?? DISPLAY_TYPE_MINIMAL}
            page={page}
            pageSize={pageSize}
            sortBy={sortBy}
            isNewlyAdded={true}
            isAllTitles={false}
            visibleFilterGroups={visibleFilterGroups}
            buildQueryKey={aggregationsQueryKeyBuilder}
            fetchAggregations={fetchAggregations}
            fetchAggregationsQuickSearch={fetchAggregationsQuickSearch}
        />
    );
};
